import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Enums } from "@bednbody/types/database";
import { supabase } from "@/lib/supabase";
import { callEdgeFunction } from "@/lib/edge-functions";

/**
 * Roles assignable from the roster UI. `superadmin` is deliberately absent:
 * who may mint a superadmin is not defined in the vault, so that authority
 * stays gated out of the UI (never-invent-policy.md, SECURITY §9a) — the DB
 * grant is unchanged. `client` is not a staff role.
 */
export const ASSIGNABLE_STAFF_ROLES = [
  "therapist",
  "rider",
  "hub_manager",
  "admin",
  "executive",
] as const satisfies readonly Enums<"user_role">[];

export type AssignableStaffRole = (typeof ASSIGNABLE_STAFF_ROLES)[number];

/**
 * Roster listing. `hubId = null` means all hubs — usable only by hr/admin/
 * executive/superadmin (staff_admin_read); a hub_manager passes their own
 * hub and RLS `staff_hub_read` scopes them regardless of what the UI asks
 * for. Keyed under "hub-staff" so the existing staff mutations' prefix
 * invalidations refresh this too.
 */
export function useRosterStaff(hubId: string | null) {
  return useQuery({
    queryKey: ["hub-staff", "roster", hubId],
    queryFn: async () => {
      let query = supabase
        .from("staff")
        .select(
          "id, full_name, email, role, active, hub_id, rider_rank, therapist_rank, therapist_code, rank_is_manual, hubs ( name )",
        )
        .order("full_name");
      if (hubId) query = query.eq("hub_id", hubId);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []).map((s) => ({
        id: s.id,
        name: s.full_name,
        email: s.email,
        role: s.role,
        active: s.active,
        status: s.active ? ("active" as const) : ("inactive" as const),
        hubId: s.hub_id,
        hubName: s.hubs?.name ?? null,
        // Meaningless for non-riders (0062:38) — surfaced anyway rather than
        // nulled here, because the Riders tab is the only place it is shown and
        // hiding it in the mapper would put that decision two files from the
        // column that renders it.
        rider_rank: s.rider_rank,
        // Same reasoning for the therapist column (0074:101): meaningless for
        // non-therapists, surfaced anyway so the Therapists tab decides.
        therapist_rank: s.therapist_rank,
        // 0329 — true when a person last set the rank; the monthly apply skips
        // flagged rows until an admin releases them back to the system.
        rank_is_manual: s.rank_is_manual,
        // Public, immutable, therapists-only (0033) — null for every other role.
        therapist_code: s.therapist_code,
      }));
    },
    staleTime: 60_000,
  });
}

/**
 * Add employee = ONE server call that invites them and creates their row.
 *
 * ═══ THIS NO LONGER TAKES A PASSWORD, DELIBERATELY ═══
 *
 * It used to: an operator typed the new therapist's password into this form,
 * a client-side GoTrue `signUp` created the account, and a separate insert
 * added the `staff` row. Two defects, both now closed server-side:
 *
 *   1. The operator knew a credential that let them act AS that employee.
 *      Nobody but the invitee ever holds it now — GoTrue emails an invite link
 *      and they set their own password.
 *   2. The two writes were not atomic. A failed insert left an orphan auth
 *      user that blocked re-using the email and had to be deleted by hand in
 *      Studio. `POST /admin/staff` cleans it up itself.
 *
 * `service_role` still never reaches this bundle (rules/security.md) — the
 * admin Edge Function holds it, which is exactly where it belongs. Authority
 * is unchanged: hr/admin/superadmin, matching 0003's `insert on staff` grant.
 */
export function useCreateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      fullName: string;
      email: string;
      role: AssignableStaffRole;
      hubId: string | null;
      /** Super Admin only; omitted = the emailed invite. */
      password?: string;
    }) => {
      return callEdgeFunction<{ id: string; role: AssignableStaffRole; active: boolean }>(
        "admin/staff",
        { data: input },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hub-staff"] });
    },
  });
}

/**
 * Promote / change role. Plain RLS write: staff_admin_write (0003) grants
 * hr/admin/superadmin full update on `staff`. The new role reaches the
 * employee's JWT at their next sign-in or token refresh (access-token hook,
 * 0014); if the new role is hub_manager/hr/admin/executive/superadmin, the
 * MFA wall (0021) makes them enrol TOTP before the PostgREST role activates.
 */
export function usePromoteStaffRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { staffId: string; role: AssignableStaffRole }) => {
      const { data, error } = await supabase
        .from("staff")
        .update({ role: input.role })
        .eq("id", input.staffId)
        .select("id, role")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hub-staff"] });
      queryClient.invalidateQueries({ queryKey: ["attendance"] });
    },
  });
}

/**
 * Plain client write on purpose: `staff_hub_toggle` RLS (0003) already scopes
 * a Hub Manager's update to `active` on their own hub_id, and admin/hr/
 * superadmin have an unrestricted update grant — no Edge Function needed,
 * this single-column toggle carries no cross-table rule (API Conventions:
 * "simple reads/writes with no transaction stay on the client").
 */
export function useToggleStaffActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { staffId: string; active: boolean }) => {
      const { data, error } = await supabase
        .from("staff")
        .update({ active: input.active })
        .eq("id", input.staffId)
        .select("id, active")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hub-staff"] });
    },
  });
}

/**
 * BL-39 — correct a staff member's name. Same plain RLS write as role and hub:
 * staff_admin_write (0003) grants hr/admin/superadmin full update, and a hub
 * manager's column grant covers only `active`, so the DB refuses them anyway.
 *
 * Name only. `email` is the login identity in GoTrue, and changing the copy on
 * `staff` alone would leave the two disagreeing — that needs a server route,
 * so the detail modal shows email read-only.
 */
export function useRenameStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { staffId: string; fullName: string }) => {
      const { data, error } = await supabase
        .from("staff")
        .update({ full_name: input.fullName })
        .eq("id", input.staffId)
        .select("id, full_name")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hub-staff"] });
    },
  });
}

/**
 * "Reassign staff between hubs" (User Stories — Admin §Admin). Plain RLS
 * write: staff_admin_write (0003) grants hr/admin/superadmin full update;
 * hub_manager's column grant covers only `active`, so this fails for them at
 * the DB even if the UI leaked the button.
 */
export function useReassignStaffHub() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { staffId: string; hubId: string }) => {
      const { data, error } = await supabase
        .from("staff")
        .update({ hub_id: input.hubId })
        .eq("id", input.staffId)
        .select("id, hub_id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hub-staff"] });
      queryClient.invalidateQueries({ queryKey: ["attendance"] });
    },
  });
}

/**
 * RIDER-031/032 — change a rider's rank.
 *
 * ⚠ NOT A PLAIN CLIENT WRITE, unlike role/hub/active above, and the difference
 * is structural rather than stylistic. 0062 grants hr/admin/superadmin
 * table-wide UPDATE on `staff`, and Postgres cannot revoke one column back out
 * of a table grant — so a `.update({ rider_rank })` from here would SUCCEED at
 * the privilege layer and then be refused by `enforce_rider_rank_audit`, which
 * rejects any rank change not made through `set_rider_rank()`.
 *
 * The Edge route is the path that satisfies the trigger, and it is also the only
 * one that can record WHO made the change: the SQL function runs under
 * service_role where auth.uid() is NULL, so the acting admin has to be resolved
 * from the verified JWT server-side and handed in.
 */
/**
 * Staff password reset, the fallback for a lost or changed phone (Leon,
 * 2026-10-17). Admin and Superadmin; the Edge route checks again and audits.
 * Returns the temporary password once — the caller shows it and forgets it.
 */
export function useResetStaffPassword() {
  return useMutation({
    mutationFn: async (input: { staffId: string; identityCheck: string }) =>
      callEdgeFunction<{ temporaryPassword: string }>(
        `admin/staff/${input.staffId}/reset-password`,
        { data: { identityCheck: input.identityCheck } },
      ),
  });
}

export function useSetRiderRank() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { staffId: string; rank: string; reason: string }) => {
      return callEdgeFunction<{ id: string; riderRank: string }>(
        `admin/staff/${input.staffId}/rider-rank`,
        { data: { rank: input.rank, reason: input.reason } },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hub-staff"] });
    },
  });
}

/**
 * TSESSION-012…015 — change a therapist's rank.
 *
 * ⚠ SUPERADMIN ONLY, WHERE THE RIDER EQUIVALENT ABOVE IS ADMIN. The asymmetry
 * is the product spec's, not ours: RIDER-031 names "an authorized Admin" for
 * riders, and nothing anywhere names anyone for therapists (OQ-80). 0074
 * granted `set_therapist_rank()` to service_role alone and the Edge route gates
 * on `superadmin`, so an admin calling this gets FORBIDDEN — the caller must
 * hide the action rather than let it fail at the server.
 *
 * Everything else matches the rider hook: a plain `.update({ therapist_rank })`
 * would pass the table grant and then be refused by `enforce_therapist_rank_audit`,
 * and only the Edge route can resolve the acting person from the verified JWT
 * for the audit record.
 */
export function useSetTherapistRank() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { staffId: string; rank: string; reason: string }) => {
      return callEdgeFunction<{ id: string; therapistRank: string }>(
        `admin/staff/${input.staffId}/therapist-rank`,
        { data: { rank: input.rank, reason: input.reason } },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hub-staff"] });
    },
  });
}

/**
 * 0329 — the staff member's most recent computed monthly score, for the rank
 * dialog's "what did the system last say" line. Plain RLS read: 0244 grants
 * `staff_tier_scores` select to admin/superadmin only.
 */
export function useLatestTierScore(staffId: string | null) {
  return useQuery({
    enabled: staffId !== null,
    queryKey: ["hub-staff", "tier-score", staffId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff_tier_scores")
        .select("cycle_month, total_score, tier, is_complete")
        .eq("staff_id", staffId!)
        .order("cycle_month", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });
}

/**
 * 0329 — hand a manually-set rank back to the monthly system. Audited like a
 * rank change (reason required); the rank itself moves at the next monthly
 * apply, which then owns it again. Edge route for the same reasons as the
 * setters: `enforce_*_rank_audit` guards the flag's neighbors and only the
 * Edge path can resolve the acting admin for the audit record.
 */
export function useReleaseRankToSystem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { staffId: string; reason: string }) => {
      return callEdgeFunction<{ id: string; rankIsManual: boolean }>(
        `admin/staff/${input.staffId}/release-rank`,
        { data: { reason: input.reason } },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hub-staff"] });
    },
  });
}
